import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardMedia,
    CardActions,
    Typography,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Alert,
    Skeleton,
    Tooltip,
    Tabs,
    Tab,
    Divider,
    InputAdornment,
    CircularProgress,
    Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SellIcon from '@mui/icons-material/Sell';
import SchoolIcon from '@mui/icons-material/School';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BusinessIcon from '@mui/icons-material/Business';
import { useTranslate, useNotify } from 'react-admin';
import {
    ListingScope,
    filterListingsByScope,
    hasGroupListings,
    isGroupListing,
    purchaseButtonLabel,
    canLicenseCourses,
} from './marketplaceVisibility';
import tokenService from '../services/tokenService';

// Ensure API_URL ends with /api
const baseApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_URL = baseApiUrl.endsWith('/api') ? baseApiUrl : `${baseApiUrl}/api`;

// Deutsche Beschriftung der Schwierigkeitsgrade (analog resources/learningPaths.tsx)
const DIFFICULTY_LABELS: Record<string, string> = {
    beginner: 'Einstieg',
    intermediate: 'Vertiefung',
    advanced: 'Spezialisierung',
};

interface MarketplaceListing {
    id: number;
    uuid: string;
    title: string;
    description: string | null;
    thumbnailUrl: string | null;
    course: {
        id: number;
        title?: string;
        difficulty?: string;
        estimatedDuration?: number;
        lessonCount?: number;
    };
    priceReference: string | null;
    priceCopy: string | null;
    currency: string;
    allowReference: boolean;
    allowCopy: boolean;
    allowSeatCopy: boolean;
    priceSeat: string | null;
    revenueSharePercent?: string;
    isFeatured: boolean;
    isActive?: boolean;
    tags: string[] | null;
    highlights: string[] | null;
    previewVideoUrl: string | null;
    totalLicenses: number;
    averageRating: string | null;
    alreadyLicensed?: boolean;
    companyId: number;
    createdAt?: string;
    visibility?: 'public' | 'group';
}

const LICENSE_STATUS_LABELS: Record<string, string> = {
    pending: 'Ausstehend',
    active: 'Aktiv',
    suspended: 'Pausiert',
    expired: 'Abgelaufen',
    revoked: 'Widerrufen',
};

const getLicenseStatusLabel = (status: string) => LICENSE_STATUS_LABELS[status] ?? status;

interface CourseLicense {
    id: number;
    uuid: string;
    licenseType: string;
    status: string;
    pricePaid: string;
    licensorRevenue?: string;
    platformFee?: string;
    currency: string;
    maxEnrollments: number | null;
    currentEnrollments: number;
    expiresAt: string | null;
    autoSyncUpdates: boolean;
    lastSyncedAt: string | null;
    createdAt: string;
    sourceCourse: {
        id: number;
        title: string;
        thumbnailUrl?: string;
    };
    licensedCourse?: {
        id: number;
        title: string;
    } | null;
    listing?: {
        id: number;
        title: string;
    };
    licensorCompanyId?: number;
    licenseeCompanyId?: number;
}

interface Course {
    id: number;
    title: string;
    isActive: boolean;
}

const MarketplacePage: React.FC = () => {
    const translate = useTranslate();
    const notify = useNotify();

    const canLicense = canLicenseCourses(tokenService.getRoles());

    const [tabIndex, setTabIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Browse state
    const [listings, setListings] = useState<MarketplaceListing[]>([]);
    const [browseScope, setBrowseScope] = useState<ListingScope>('all');
    const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
    const [selectedListing, setSelectedListing] = useState<MarketplaceListing | null>(null);
    const [purchaseType, setPurchaseType] = useState<'reference' | 'copy' | 'seat_copy'>('reference');
    // Teilnehmer-Lizenz: gewuenschte Platzzahl (Preis = seats * priceSeat)
    const [seatCount, setSeatCount] = useState(10);
    const [ownCatalogs, setOwnCatalogs] = useState<{ id: number; name: string }[]>([]);
    const [targetCatalogId, setTargetCatalogId] = useState<number | ''>('');
    const [purchasing, setPurchasing] = useState(false);

    // My Listings state
    const [myListings, setMyListings] = useState<MarketplaceListing[]>([]);
    const [listingDialogOpen, setListingDialogOpen] = useState(false);
    const [editingListing, setEditingListing] = useState<MarketplaceListing | null>(null);
    const [courses, setCourses] = useState<Course[]>([]);
    const [listingFormData, setListingFormData] = useState({
        courseId: null as number | null,
        title: '',
        description: '',
        priceReference: '0.00',
        priceCopy: '0.00',
        priceSeat: '0.00',
        currency: 'EUR',
        allowReference: true,
        allowCopy: true,
        allowSeatCopy: false,
        revenueSharePercent: '70.00',
        isActive: true,
        visibility: 'public' as 'public' | 'group',
        tags: [] as string[],
        highlights: [] as string[],
    });

    // Licenses state
    const [myLicenses, setMyLicenses] = useState<CourseLicense[]>([]);

    // Sales state
    const [mySales, setMySales] = useState<CourseLicense[]>([]);
    const [revenueStats, setRevenueStats] = useState<any[]>([]);

    const getAuthToken = () => localStorage.getItem('token');

    const fetchMarketplace = useCallback(async () => {
        try {
            const token = getAuthToken();
            const response = await fetch(`${API_URL}/marketplace/browse`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('Failed to fetch marketplace');
            const data = await response.json();
            setListings(data.listings || []);
        } catch (err) {
            console.error('Error fetching marketplace:', err);
            setError('Fehler beim Laden des Marktplatzes');
        }
    }, []);

    const fetchMyListings = useCallback(async () => {
        try {
            const token = getAuthToken();
            const response = await fetch(`${API_URL}/marketplace/my-listings`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('Failed to fetch my listings');
            const data = await response.json();
            setMyListings(data.listings || []);
        } catch (err) {
            console.error('Error fetching my listings:', err);
        }
    }, []);

    const fetchMyLicenses = useCallback(async () => {
        try {
            const token = getAuthToken();
            const response = await fetch(`${API_URL}/marketplace/licenses`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('Failed to fetch licenses');
            const data = await response.json();
            setMyLicenses(data.licenses || []);
        } catch (err) {
            console.error('Error fetching licenses:', err);
        }
    }, []);

    const fetchMySales = useCallback(async () => {
        try {
            const token = getAuthToken();
            const response = await fetch(`${API_URL}/marketplace/sales`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('Failed to fetch sales');
            const data = await response.json();
            setMySales(data.licenses || []);
            setRevenueStats(data.revenue || []);
        } catch (err) {
            console.error('Error fetching sales:', err);
        }
    }, []);

    const fetchCourses = useCallback(async () => {
        try {
            const token = getAuthToken();
            const response = await fetch(`${API_URL}/courses?isActive=true&order[title]=ASC&itemsPerPage=100`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/ld+json',
                },
            });
            if (!response.ok) {
                throw new Error('Failed to fetch courses');
            }
            const data = await response.json();
            setCourses(data['hydra:member'] || data.member || []);
        } catch (err) {
            console.error('Error fetching courses:', err);
        }
    }, []);

    const fetchOwnCatalogs = useCallback(async () => {
        try {
            const token = getAuthToken();
            const response = await fetch(`${API_URL}/course_catalogs?companyId=${tokenService.getCompanyId()}&itemsPerPage=100`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/ld+json',
                },
            });
            if (!response.ok) {
                throw new Error('Failed to fetch catalogs');
            }
            const data = await response.json();
            const catalogs: { id: number; name: string; isActive: boolean }[] = data['hydra:member'] || data.member || [];
            setOwnCatalogs(catalogs
                .filter((catalog) => catalog.isActive && catalog.name !== 'Lizenzierte Kurse')
                .sort((a, b) => a.name.localeCompare(b.name)));
        } catch (err) {
            console.error('Error fetching catalogs:', err);
        }
    }, []);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([
                fetchMarketplace(),
                fetchMyListings(),
                fetchCourses(),
                ...(canLicense ? [fetchMyLicenses(), fetchMySales(), fetchOwnCatalogs()] : []),
            ]);
            setLoading(false);
        };
        loadData();
    }, [canLicense, fetchMarketplace, fetchMyListings, fetchMyLicenses, fetchMySales, fetchCourses, fetchOwnCatalogs]);

    const openPurchaseDialog = (listing: MarketplaceListing) => {
        setSelectedListing(listing);
        setPurchaseType(listing.allowReference ? 'reference' : listing.allowCopy ? 'copy' : listing.allowSeatCopy ? 'seat_copy' : 'reference');
        setTargetCatalogId('');
        setPurchaseDialogOpen(true);
    };

    const handlePurchase = async () => {
        if (!selectedListing) return;

        setPurchasing(true);
        try {
            const token = getAuthToken();
            const response = await fetch(`${API_URL}/marketplace/purchase`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    listingId: selectedListing.id,
                    licenseType: purchaseType,
                    ...(purchaseType === 'seat_copy' ? { seats: seatCount } : {}),
                    ...(purchaseType !== 'reference' && targetCatalogId !== '' ? { catalogId: targetCatalogId } : {}),
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Purchase failed');
            }

            notify('Lizenz erfolgreich erworben!', { type: 'success' });
            setPurchaseDialogOpen(false);
            setSelectedListing(null);

            // Refresh data
            await Promise.all([fetchMarketplace(), fetchMyLicenses()]);
        } catch (err: any) {
            notify(err.message || 'Fehler beim Kauf', { type: 'error' });
        } finally {
            setPurchasing(false);
        }
    };

    const handleCreateListing = async () => {
        if (!listingFormData.courseId) {
            notify('Bitte wählen Sie einen Kurs aus', { type: 'warning' });
            return;
        }

        try {
            const token = getAuthToken();
            const response = await fetch(`${API_URL}/marketplace/listings`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(listingFormData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to create listing');
            }

            notify('Kurs erfolgreich veröffentlicht!', { type: 'success' });
            setListingDialogOpen(false);
            resetListingForm();
            await fetchMyListings();
        } catch (err: any) {
            notify(err.message || 'Fehler beim Veröffentlichen', { type: 'error' });
        }
    };

    const handleUpdateListing = async () => {
        if (!editingListing) return;

        try {
            const token = getAuthToken();
            const response = await fetch(`${API_URL}/marketplace/listings/${editingListing.id}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    title: listingFormData.title || null,
                    description: listingFormData.description || null,
                    priceReference: listingFormData.priceReference,
                    priceCopy: listingFormData.priceCopy,
                    priceSeat: listingFormData.priceSeat,
                    currency: listingFormData.currency,
                    allowReference: listingFormData.allowReference,
                    allowCopy: listingFormData.allowCopy,
                    allowSeatCopy: listingFormData.allowSeatCopy,
                    revenueSharePercent: listingFormData.revenueSharePercent,
                    isActive: listingFormData.isActive,
                    visibility: listingFormData.visibility,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to update listing');
            }

            notify('Listing aktualisiert!', { type: 'success' });
            setListingDialogOpen(false);
            setEditingListing(null);
            resetListingForm();
            await fetchMyListings();
        } catch (err: any) {
            notify(err.message || 'Fehler beim Aktualisieren', { type: 'error' });
        }
    };

    const handleDeleteListing = async (listing: MarketplaceListing) => {
        if (!window.confirm('Sind Sie sicher, dass Sie dieses Listing löschen möchten?')) {
            return;
        }

        try {
            const token = getAuthToken();
            const response = await fetch(`${API_URL}/marketplace/listings/${listing.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to delete listing');
            }

            notify('Listing gelöscht!', { type: 'success' });
            await fetchMyListings();
        } catch (err: any) {
            notify(err.message || 'Fehler beim Löschen', { type: 'error' });
        }
    };

    const resetListingForm = () => {
        setListingFormData({
            courseId: null,
            title: '',
            description: '',
            priceReference: '0.00',
            priceCopy: '0.00',
            priceSeat: '0.00',
            currency: 'EUR',
            allowReference: true,
            allowCopy: true,
            allowSeatCopy: false,
            revenueSharePercent: '70.00',
            isActive: true,
            visibility: 'public',
            tags: [],
            highlights: [],
        });
    };

    const openEditListing = (listing: MarketplaceListing) => {
        setEditingListing(listing);
        setListingFormData({
            courseId: listing.course.id,
            title: listing.title || '',
            description: listing.description || '',
            priceReference: listing.priceReference || '0.00',
            priceCopy: listing.priceCopy || '0.00',
            priceSeat: listing.priceSeat || '0.00',
            currency: listing.currency,
            allowReference: listing.allowReference,
            allowCopy: listing.allowCopy,
            allowSeatCopy: listing.allowSeatCopy ?? false,
            revenueSharePercent: listing.revenueSharePercent || '70.00',
            isActive: listing.isActive ?? true,
            visibility: listing.visibility || 'public',
            tags: listing.tags || [],
            highlights: listing.highlights || [],
        });
        setListingDialogOpen(true);
    };

    const formatPrice = (amount: string | null, currency: string) => {
        if (!amount || parseFloat(amount) === 0) return 'Kostenlos';
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: currency,
        }).format(parseFloat(amount));
    };

    const visibleListings = filterListingsByScope(listings, browseScope);

    const renderBrowseTab = () => (
        <Box>
            {/* Gruppen-Bereich: nur anbieten, wenn der Mandant ueberhaupt
                gruppeninterne Angebote sieht (Teil einer Unternehmensgruppe) */}
            {hasGroupListings(listings) && (
                <Box sx={{ mb: 3, display: 'flex', gap: 1 }}>
                    <Chip
                        label="Alle Angebote"
                        color={browseScope === 'all' ? 'primary' : 'default'}
                        onClick={() => setBrowseScope('all')}
                    />
                    <Chip
                        icon={<BusinessIcon />}
                        label="Meine Unternehmensgruppe"
                        color={browseScope === 'group' ? 'primary' : 'default'}
                        onClick={() => setBrowseScope('group')}
                    />
                </Box>
            )}
            {visibleListings.length === 0 ? (
                <Alert severity="info">
                    {browseScope === 'group'
                        ? 'Keine gruppeninternen Angebote verfügbar.'
                        : 'Keine Kurse im Marktplatz verfügbar.'}
                </Alert>
            ) : (
                <Grid container spacing={3}>
                    {visibleListings.map((listing) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={listing.id}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                {listing.thumbnailUrl && (
                                    <CardMedia
                                        component="img"
                                        height="160"
                                        image={listing.thumbnailUrl}
                                        alt={listing.title}
                                    />
                                )}
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Typography variant="h6" component="h3" gutterBottom>
                                            {listing.title}
                                        </Typography>
                                        {listing.alreadyLicensed && (
                                            <Chip
                                                icon={<CheckCircleIcon />}
                                                label="Lizenziert"
                                                color="success"
                                                size="small"
                                            />
                                        )}
                                    </Box>

                                    {listing.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {listing.description.substring(0, 100)}...
                                        </Typography>
                                    )}

                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                        {listing.course.lessonCount && (
                                            <Chip
                                                size="small"
                                                label={`${listing.course.lessonCount} Lektionen`}
                                                variant="outlined"
                                            />
                                        )}
                                        {listing.course.difficulty && (
                                            <Chip
                                                size="small"
                                                label={DIFFICULTY_LABELS[listing.course.difficulty] || listing.course.difficulty}
                                                variant="outlined"
                                            />
                                        )}
                                        {listing.isFeatured && (
                                            <Chip
                                                size="small"
                                                label="Featured"
                                                color="primary"
                                            />
                                        )}
                                        {isGroupListing(listing) && (
                                            <Chip
                                                size="small"
                                                icon={<BusinessIcon />}
                                                label="Unternehmensgruppe"
                                                color="info"
                                            />
                                        )}
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                            {listing.allowReference && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Referenz: {formatPrice(listing.priceReference, listing.currency)}
                                                </Typography>
                                            )}
                                            {listing.allowSeatCopy && listing.priceSeat && (
                                                <Typography variant="caption" display="block">
                                                    Teilnehmer: {formatPrice(listing.priceSeat, listing.currency)} / Platz
                                                </Typography>
                                            )}
                                            {listing.allowCopy && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Kopie: {formatPrice(listing.priceCopy, listing.currency)}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Typography variant="caption" color="text.secondary">
                                            {listing.totalLicenses} Lizenzen
                                        </Typography>
                                    </Box>
                                </CardContent>
                                <CardActions>
                                    <Button
                                        size="small"
                                        startIcon={<VisibilityIcon />}
                                        onClick={() => openPurchaseDialog(listing)}
                                    >
                                        Details
                                    </Button>
                                    {canLicense && !listing.alreadyLicensed && (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<ShoppingCartIcon />}
                                            onClick={() => openPurchaseDialog(listing)}
                                        >
                                            Lizenzieren
                                        </Button>
                                    )}
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );

    const renderMyListingsTab = () => (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditingListing(null);
                        resetListingForm();
                        setListingDialogOpen(true);
                    }}
                >
                    Kurs veröffentlichen
                </Button>
            </Box>

            {myListings.length === 0 ? (
                <Alert severity="info">
                    Sie haben noch keine Kurse im Marktplatz veröffentlicht.
                </Alert>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Kurs</TableCell>
                                <TableCell>Preis (Referenz)</TableCell>
                                <TableCell>Preis (Kopie)</TableCell>
                                <TableCell>Revenue Share</TableCell>
                                <TableCell>Lizenzen</TableCell>
                                <TableCell>Sichtbarkeit</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {myListings.map((listing) => (
                                <TableRow key={listing.id}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <SchoolIcon color="action" />
                                            {listing.title}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {listing.allowReference ? formatPrice(listing.priceReference, listing.currency) : '-'}
                                    </TableCell>
                                    <TableCell>
                                        {listing.allowCopy ? formatPrice(listing.priceCopy, listing.currency) : '-'}
                                    </TableCell>
                                    <TableCell>{listing.revenueSharePercent}%</TableCell>
                                    <TableCell>{listing.totalLicenses}</TableCell>
                                    <TableCell>
                                        {isGroupListing(listing) ? (
                                            <Chip
                                                size="small"
                                                icon={<BusinessIcon />}
                                                label="Unternehmensgruppe"
                                                color="info"
                                            />
                                        ) : (
                                            <Chip size="small" label="Öffentlich" variant="outlined" />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={listing.isActive ? 'Aktiv' : 'Inaktiv'}
                                            color={listing.isActive ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            onClick={() => openEditListing(listing)}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDeleteListing(listing)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );

    const renderLicensesTab = () => (
        <Box>
            {myLicenses.length === 0 ? (
                <Alert severity="info">
                    Sie haben noch keine Kurse lizenziert.
                </Alert>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Kurs</TableCell>
                                <TableCell>Lizenztyp</TableCell>
                                <TableCell>Preis</TableCell>
                                <TableCell>Einschreibungen</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Datum</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {myLicenses.map((license) => (
                                <TableRow key={license.id}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <SchoolIcon color="action" />
                                            <Box>
                                                <Typography variant="body2">
                                                    {license.sourceCourse.title}
                                                </Typography>
                                                {license.licensedCourse && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Kopie: {license.licensedCourse.title}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={license.licenseType === 'reference' ? 'Referenz' : license.licenseType === 'seat_copy' ? 'Teilnehmer' : 'Kopie'}
                                            color={license.licenseType === 'reference' ? 'primary' : 'secondary'}
                                            size="small"
                                            icon={license.licenseType === 'reference' ? <SyncIcon /> : undefined}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {formatPrice(license.pricePaid, license.currency)}
                                    </TableCell>
                                    <TableCell>
                                        {license.maxEnrollments
                                            ? `${license.currentEnrollments} / ${license.maxEnrollments}`
                                            : `${license.currentEnrollments} (unbegrenzt)`}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getLicenseStatusLabel(license.status)}
                                            color={license.status === 'active' ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {new Date(license.createdAt).toLocaleDateString('de-DE')}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );

    const renderSalesTab = () => (
        <Box>
            {/* Revenue Summary */}
            {revenueStats.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2}>
                        {revenueStats.map((stat, index) => (
                            <Grid size={{ xs: 12, sm: 4 }} key={index}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" color="primary">
                                            {formatPrice(stat.totalRevenue, stat.currency)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Gesamtumsatz ({stat.licenseCount} Lizenzen)
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {mySales.length === 0 ? (
                <Alert severity="info">
                    Sie haben noch keine Kurse verkauft.
                </Alert>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Kurs</TableCell>
                                <TableCell>Lizenztyp</TableCell>
                                <TableCell>Verkaufspreis</TableCell>
                                <TableCell>Ihr Anteil</TableCell>
                                <TableCell>Plattformgebühr</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Datum</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {mySales.map((sale) => (
                                <TableRow key={sale.id}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <SellIcon color="action" />
                                            {sale.sourceCourse.title}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={sale.licenseType === 'reference' ? 'Referenz' : sale.licenseType === 'seat_copy' ? 'Teilnehmer' : 'Kopie'}
                                            color={sale.licenseType === 'reference' ? 'primary' : 'secondary'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {formatPrice(sale.pricePaid, sale.currency)}
                                    </TableCell>
                                    <TableCell>
                                        <Typography color="success.main" fontWeight="bold">
                                            {formatPrice(sale.licensorRevenue || '0', sale.currency)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {formatPrice(sale.platformFee || '0', sale.currency)}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getLicenseStatusLabel(sale.status)}
                                            color={sale.status === 'active' ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {new Date(sale.createdAt).toLocaleDateString('de-DE')}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={400} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StorefrontIcon />
                {translate('marketplace.title', { _: 'Marktplatz' })}
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={tabIndex}
                    onChange={(_, newValue) => setTabIndex(newValue)}
                    variant="fullWidth"
                >
                    <Tab
                        value={0}
                        icon={<StorefrontIcon />}
                        label={translate('marketplace.browse', { _: 'Marktplatz durchsuchen' })}
                    />
                    <Tab
                        value={1}
                        icon={<SellIcon />}
                        label={translate('marketplace.myListings', { _: 'Meine Angebote' })}
                    />
                    {canLicense && (
                        <Tab
                            value={2}
                            icon={<ReceiptIcon />}
                            label={translate('marketplace.myLicenses', { _: 'Meine Lizenzen' })}
                        />
                    )}
                    {canLicense && (
                        <Tab
                            value={3}
                            icon={<SellIcon />}
                            label={translate('marketplace.mySales', { _: 'Meine Verkäufe' })}
                        />
                    )}
                </Tabs>
            </Paper>

            <Box sx={{ mt: 3 }}>
                {tabIndex === 0 && renderBrowseTab()}
                {tabIndex === 1 && renderMyListingsTab()}
                {canLicense && tabIndex === 2 && renderLicensesTab()}
                {canLicense && tabIndex === 3 && renderSalesTab()}
            </Box>

            {/* Purchase Dialog */}
            <Dialog
                open={purchaseDialogOpen}
                onClose={() => setPurchaseDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Kurs lizenzieren</DialogTitle>
                <DialogContent>
                    {selectedListing && (
                        <Box sx={{ pt: 1 }}>
                            <Typography variant="h6" gutterBottom>
                                {selectedListing.title}
                            </Typography>

                            {selectedListing.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {selectedListing.description}
                                </Typography>
                            )}

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle2" gutterBottom>
                                Lizenztyp wählen:
                            </Typography>

                            <FormControl fullWidth sx={{ mt: 1 }}>
                                <Select
                                    value={purchaseType}
                                    onChange={(e) => setPurchaseType(e.target.value as 'reference' | 'copy' | 'seat_copy')}
                                >
                                    {selectedListing.allowReference && (
                                        <MenuItem value="reference">
                                            <Box>
                                                <Typography>Referenz-Lizenz</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Live-Verknüpfung, Updates werden automatisch synchronisiert
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    )}
                                    {selectedListing.allowCopy && (
                                        <MenuItem value="copy">
                                            <Box>
                                                <Typography>Kopie-Lizenz</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Unabhängige Kopie, Sie können den Kurs anpassen
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    )}
                                    {selectedListing.allowSeatCopy && (
                                        <MenuItem value="seat_copy">
                                            <Box>
                                                <Typography>Teilnehmer-Lizenz</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Kopie mit fester Teilnehmerzahl — Preis pro Platz
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>

                            {purchaseType === 'seat_copy' && (
                                <Box sx={{ mt: 2 }}>
                                    <TextField
                                        label="Anzahl Teilnehmer-Plätze"
                                        type="number"
                                        size="small"
                                        value={seatCount}
                                        onChange={(e) => setSeatCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                                        inputProps={{ min: 1 }}
                                        sx={{ width: 220 }}
                                    />
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                        Jeder Teilnehmer, der eingeschrieben wird und beginnt, belegt dauerhaft
                                        einen Platz — Plätze gestarteter Teilnehmer werden bei Ausschreibung
                                        nicht wieder frei.
                                    </Typography>
                                </Box>
                            )}

                            {purchaseType !== 'reference' && (
                                <TextField
                                    select
                                    fullWidth
                                    label="Zielkatalog"
                                    value={targetCatalogId}
                                    onChange={(e) => setTargetCatalogId(e.target.value === '' ? '' : Number(e.target.value))}
                                    helperText="In diesem Katalog Ihrer Kursverwaltung wird die Kopie abgelegt."
                                    SelectProps={{ displayEmpty: true }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ mt: 2 }}
                                >
                                    <MenuItem value="">Lizenzierte Kurse (Standard)</MenuItem>
                                    {ownCatalogs.map((catalog) => (
                                        <MenuItem key={catalog.id} value={catalog.id}>{catalog.name}</MenuItem>
                                    ))}
                                </TextField>
                            )}

                            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                                <Typography variant="h5" color="primary">
                                    {purchaseType === 'reference'
                                        ? formatPrice(selectedListing.priceReference, selectedListing.currency)
                                        : purchaseType === 'seat_copy'
                                            ? formatPrice((parseFloat(selectedListing.priceSeat || '0') * seatCount).toFixed(2), selectedListing.currency)
                                            : formatPrice(selectedListing.priceCopy, selectedListing.currency)}
                                </Typography>
                                {purchaseType === 'seat_copy' && (
                                    <Typography variant="caption" color="text.secondary">
                                        {seatCount} × {formatPrice(selectedListing.priceSeat, selectedListing.currency)} pro Platz
                                    </Typography>
                                )}
                            </Box>

                            {selectedListing.alreadyLicensed && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    Sie haben diesen Kurs bereits lizenziert.
                                </Alert>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPurchaseDialogOpen(false)}>
                        Abbrechen
                    </Button>
                    {canLicense && (
                        <Button
                            variant="contained"
                            onClick={handlePurchase}
                            disabled={purchasing || selectedListing?.alreadyLicensed}
                            startIcon={purchasing ? <CircularProgress size={20} /> : <ShoppingCartIcon />}
                        >
                            {purchaseButtonLabel(
                                purchaseType === 'reference'
                                    ? selectedListing?.priceReference
                                    : purchaseType === 'seat_copy'
                                        ? (parseFloat(selectedListing?.priceSeat || '0') * seatCount).toFixed(2)
                                        : selectedListing?.priceCopy,
                                purchasing,
                            )}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Create/Edit Listing Dialog */}
            <Dialog
                open={listingDialogOpen}
                onClose={() => setListingDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {editingListing ? 'Listing bearbeiten' : 'Kurs im Marktplatz veröffentlichen'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <Grid container spacing={2}>
                            {!editingListing && (
                                <Grid size={12}>
                                    <Autocomplete
                                        options={courses}
                                        getOptionLabel={(option) => option.title}
                                        value={courses.find(c => c.id === listingFormData.courseId) || null}
                                        onChange={(_, newValue) => {
                                            setListingFormData({
                                                ...listingFormData,
                                                courseId: newValue?.id || null,
                                            });
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Kurs auswählen"
                                                required
                                            />
                                        )}
                                    />
                                </Grid>
                            )}

                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    label="Angezeigter Titel (optional)"
                                    value={listingFormData.title}
                                    onChange={(e) => setListingFormData({ ...listingFormData, title: e.target.value })}
                                    helperText="Leer lassen für Kurstitel"
                                />
                            </Grid>

                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    label="Beschreibung (optional)"
                                    multiline
                                    rows={3}
                                    value={listingFormData.description}
                                    onChange={(e) => setListingFormData({ ...listingFormData, description: e.target.value })}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Preis Referenz-Lizenz"
                                    type="number"
                                    value={listingFormData.priceReference}
                                    onChange={(e) => setListingFormData({ ...listingFormData, priceReference: e.target.value })}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">EUR</InputAdornment>,
                                    }}
                                    disabled={!listingFormData.allowReference}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Preis Kopie-Lizenz"
                                    type="number"
                                    value={listingFormData.priceCopy}
                                    onChange={(e) => setListingFormData({ ...listingFormData, priceCopy: e.target.value })}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">EUR</InputAdornment>,
                                    }}
                                    disabled={!listingFormData.allowCopy}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Preis pro Teilnehmer (Teilnehmer-Lizenz)"
                                    type="number"
                                    value={listingFormData.priceSeat}
                                    onChange={(e) => setListingFormData({ ...listingFormData, priceSeat: e.target.value })}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">EUR</InputAdornment>,
                                    }}
                                    disabled={!listingFormData.allowSeatCopy}
                                    helperText="Käufer wählt die Platzzahl; Gesamtpreis = Plätze × Preis"
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={listingFormData.allowReference}
                                            onChange={(e) => setListingFormData({ ...listingFormData, allowReference: e.target.checked })}
                                        />
                                    }
                                    label="Referenz-Lizenz erlauben"
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={listingFormData.allowCopy}
                                            onChange={(e) => setListingFormData({ ...listingFormData, allowCopy: e.target.checked })}
                                        />
                                    }
                                    label="Kopie-Lizenz erlauben"
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={listingFormData.allowSeatCopy}
                                            onChange={(e) => setListingFormData({ ...listingFormData, allowSeatCopy: e.target.checked })}
                                        />
                                    }
                                    label="Teilnehmer-Lizenz erlauben (Kopie mit fester Platzzahl)"
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Revenue Share (%)"
                                    type="number"
                                    value={listingFormData.revenueSharePercent}
                                    onChange={(e) => setListingFormData({ ...listingFormData, revenueSharePercent: e.target.value })}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    }}
                                    helperText="Ihr Anteil am Verkaufspreis"
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={listingFormData.isActive}
                                            onChange={(e) => setListingFormData({ ...listingFormData, isActive: e.target.checked })}
                                        />
                                    }
                                    label="Aktiv (im Marktplatz sichtbar)"
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel id="listing-visibility-label">Sichtbarkeit</InputLabel>
                                    <Select
                                        labelId="listing-visibility-label"
                                        label="Sichtbarkeit"
                                        value={listingFormData.visibility}
                                        onChange={(e) => setListingFormData({
                                            ...listingFormData,
                                            visibility: e.target.value as 'public' | 'group',
                                        })}
                                    >
                                        <MenuItem value="public">
                                            <Box>
                                                <Typography>Öffentlich</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Für alle Mandanten im Marktplatz sichtbar
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="group">
                                            <Box>
                                                <Typography>Nur Unternehmensgruppe</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Nur für Mandanten Ihrer Unternehmensgruppe (z. B. Tochterhäuser)
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setListingDialogOpen(false)}>
                        Abbrechen
                    </Button>
                    <Button
                        variant="contained"
                        onClick={editingListing ? handleUpdateListing : handleCreateListing}
                    >
                        {editingListing ? 'Speichern' : 'Veröffentlichen'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MarketplacePage;
